/**
 * The workspace page's pure helpers (design "Flow Workspace": Overview, Projects, Quality, Activity): the run state of a
 * project, what "Start all" / "Stop all" and the bulk actions may do for this person and what they skip, the rows of the
 * Projects table (sorting, search, filters), the Quality matrix, the alerts, and one activity timeline across projects.
 * Nothing is invented: a number the services did not send is `null`, and the page shows a calm empty state for it.
 */
import { isRunning, serviceState } from '../services/SandboxService';
import { countedItems, runItems } from '../components/sandbox/projectRun';
import { filterEvents } from './collab3Model';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The state chips of a project, worst first (the sort order of the State column). */
export const RUN_STATES = {
  failed: { rank: 0, label: 'Build failed', tone: 'bad' },
  crashed: { rank: 1, label: 'Crashed', tone: 'bad' },
  building: { rank: 2, label: 'Building', tone: 'info' },
  running: { rank: 3, label: 'Running', tone: 'ok' },
  stopped: { rank: 4, label: 'Stopped', tone: 'soon' },
  none: { rank: 5, label: 'No services', tone: 'soon' },
  unknown: { rank: 6, label: 'Unknown', tone: 'soon' },
  loading: { rank: 7, label: 'Loading', tone: 'soon' },
};

/**
 * One word for a project from its `GET /projects/:id/services` answer: `{ key, label, tone, running, total }`.
 * `undefined` = not loaded yet, `null` = vfs-service has no answer for it.
 */
export function projectRunState(answer) {
  if (answer === undefined) { return { key: 'loading', ...RUN_STATES.loading, running: 0, total: 0 }; }
  if (answer === null) { return { key: 'unknown', ...RUN_STATES.unknown, running: 0, total: 0 }; }
  // Command-line tools (utils/cliServices.js) are built, never running: not counted.
  const items = countedItems(answer.projectId, runItems(answer));
  const states = items.map(({ service }) => serviceState(service));
  const running = items.filter(({ service }) => isRunning(service)).length;
  let key = 'stopped';
  if (items.length === 0) { key = 'none'; } else if (states.includes('failed')) { key = 'failed'; } else if (states.includes('crashed')) { key = 'crashed'; } else if (states.includes('building')) { key = 'building'; } else if (running > 0) { key = 'running'; }
  return { key, ...RUN_STATES[key], running, total: items.length };
}

const memoryOf = (items) => items.reduce((sum, { service }) => sum + (isRunning(service) && Number.isFinite(service?.resources?.memoryBytes) ? service.resources.memoryBytes : 0), 0);

/**
 * What "Start all" / "Stop all" (or the same on a selection) would do: `{ eligible: [{ projectId, name, items, count,
 * memoryBytes }], skipped: [{ projectId, name, reason }] }`. Only the projects the person may run (`canRun`) and that
 * have something to start (a service that does not run) or stop (one that runs) are eligible; each other one is
 * skipped with its reason. `memoryBytes` is what the running services use now (0 when the runner did not say).
 * @param {'start'|'stop'} action
 * @param {{ projectId: string, name?: string }[]} projects
 * @param {Object<string, object|null>} answers by project id
 */
export function runPlan(action, projects, answers) {
  const eligible = [];
  const skipped = [];
  (projects || []).forEach((project) => {
    const name = project.name || project.projectId;
    const skip = (reason) => skipped.push({ projectId: project.projectId, name, reason });
    const answer = answers?.[project.projectId];
    if (!answer) { skip('its state is not known'); return; }
    if (answer.canRun === false) { skip('you can only look at it'); return; }
    const items = runItems(answer);
    if (items.length === 0) { skip('it has no services'); return; }
    const running = items.filter(({ service }) => isRunning(service));
    if (action === 'start') {
      // Command-line tools are not started to run (startProgress: built in passing): nothing to start for them.
      const counted = countedItems(answer.projectId || project.projectId, items);
      const up = counted.filter(({ service }) => isRunning(service)).length;
      if (up === counted.length) { skip('it is already running'); return; }
      eligible.push({ projectId: project.projectId, name, items, count: counted.length - up, memoryBytes: 0 });
      return;
    }
    if (running.length === 0) { skip('nothing is running'); return; }
    eligible.push({ projectId: project.projectId, name, items, count: running.length, memoryBytes: memoryOf(items) });
  });
  return { eligible, skipped };
}

/** "3 projects, 7 services" for a plan's confirm, plus what they use now when it is known (Stop). */
export function planSummary(action, plan, formatBytes) {
  const projects = plural(plan.eligible.length, 'project');
  const count = plan.eligible.reduce((sum, entry) => sum + entry.count, 0);
  const services = plural(count, 'service');
  const memory = plan.eligible.reduce((sum, entry) => sum + entry.memoryBytes, 0);
  const verb = action === 'start' ? 'will start' : `${count === 1 ? 'is' : 'are'} running and will stop`;
  const using = action === 'stop' && memory > 0 && formatBytes ? `, using about ${formatBytes(memory)} of memory` : '';
  return `${projects}: ${services} ${verb}${using}.`;
}

/**
 * Who may do a bulk action on which project: `{ eligible, skipped }` as `runPlan`. `remove`: the workspace's owner, or
 * the project's own; `share`: the project's owner (people are added by the owner); `sleep`: the projects that run in
 * this person's own sandbox (their own), since a sandbox sleeps as a whole. `start` and `stop` follow `runPlan`.
 * @param {'start'|'stop'|'sleep'|'share'|'remove'} action
 */
export function bulkEligibility(action, projects, { workspace, userSub, answers } = {}) {
  if (action === 'start' || action === 'stop') { return runPlan(action, projects, answers); }
  const eligible = [];
  const skipped = [];
  (projects || []).forEach((project) => {
    const name = project.name || project.projectId;
    const mine = Boolean(userSub) && project.owner?.sub === userSub;
    let reason = null;
    if (action === 'remove' && !(workspace?.role === 'owner' || mine)) { reason = 'only the workspace owner or the project\'s owner can remove it'; }
    if (action === 'share' && project.role !== 'owner') { reason = 'only its owner can share it'; }
    if (action === 'sleep' && !mine) { reason = 'it runs in someone else\'s sandbox'; }
    if (reason) { skipped.push({ projectId: project.projectId, name, reason }); } else { eligible.push({ projectId: project.projectId, name }); }
  });
  return { eligible, skipped };
}

/** "Started 3 projects. Skipped Billing (you can only look at it), Shop (it is already running)." */
export function reportText({ verb, done = [], skipped = [], failed = [] }) {
  const parts = [];
  parts.push(done.length ? `${verb} ${plural(done.length, 'project')}.` : 'Nothing was changed.');
  if (skipped.length) { parts.push(`Skipped ${skipped.map((entry) => `${entry.name} (${entry.reason})`).join(', ')}.`); }
  if (failed.length) { parts.push(`Failed: ${failed.map((entry) => `${entry.name} (${entry.error})`).join(', ')}.`); }
  return parts.join(' ');
}

/** Who owns a project, as the page says it: "You", else their name or email. */
export const ownerName = (project, userSub) => (userSub && project?.owner?.sub === userSub ? 'You' : project?.owner?.name || project?.owner?.email || 'Someone');

// ---- The Projects table ----

export const SORT_KEYS = ['name', 'state', 'problems', 'quality', 'owner', 'changed'];

/**
 * The table's rows from what the services answered: `{ projectId, name, project, state, services, problems, quality,
 * owner, lastChange }`. `problems`: `{ count, text }` (null count while the state is unknown); `quality`: `{ blocked,
 * text, tone }` (blocked null while unknown); `lastChange`: ms or null (no event in the feed, or not loaded).
 * @param {object[]} projects the workspace's projects
 * @param {{ answers: object, quality: object, changes: object, userSub?: string }} data
 */
export function buildRows(projects, { answers = {}, quality = {}, changes = {}, userSub = null } = {}) {
  return (projects || []).map((project) => {
    const state = projectRunState(answers[project.projectId]);
    const known = state.key !== 'loading' && state.key !== 'unknown';
    const bad = state.key === 'failed' || state.key === 'crashed';
    const entry = quality[project.projectId];
    const blocked = blockedOf(entry);
    let qualityCell = { blocked: null, text: entry?.status === 'error' ? '—' : 'Loading', tone: 'soon' };
    if (blocked !== null) {
      const neverRan = Boolean(entry.value.gate.neverRan);
      qualityCell = blocked > 0 ? { blocked, text: `${blocked} block`, tone: 'bad' } : { blocked, text: neverRan ? 'Not checked' : 'Pass', tone: neverRan ? 'soon' : 'ok' };
    }
    return {
      projectId: project.projectId,
      name: project.name || project.projectId,
      project,
      state,
      services: known ? state.total : null,
      problems: { count: known ? Number(bad) : null, text: known ? (bad ? state.label : 'None') : '—' },
      quality: qualityCell,
      owner: ownerName(project, userSub),
      lastChange: Number.isFinite(changes[project.projectId]) ? changes[project.projectId] : null,
    };
  });
}

const compareText = (a, b) => String(a).localeCompare(String(b), 'en', { sensitivity: 'base' });
const SORTERS = {
  name: (a, b) => compareText(a.name, b.name),
  state: (a, b) => a.state.rank - b.state.rank,
  problems: (a, b) => (b.problems.count ?? -1) - (a.problems.count ?? -1),
  quality: (a, b) => (b.quality.blocked ?? -1) - (a.quality.blocked ?? -1),
  owner: (a, b) => compareText(a.owner, b.owner),
  changed: (a, b) => (b.lastChange ?? -1) - (a.lastChange ?? -1),
};
// What each column is empty of, which sorts last in either direction.
const EMPTY = {
  problems: (row) => row.problems.count === null,
  quality: (row) => row.quality.blocked === null,
  changed: (row) => row.lastChange === null,
  state: (row) => row.state.key === 'loading' || row.state.key === 'unknown',
};

/**
 * The rows sorted by a column. The first order of each column is the useful one (worst state, most problems and most
 * blocking checks, latest change first, A to Z); `dir` 'desc' reverses it. Rows with nothing in that column stay last.
 * No column: the workspace's own order.
 */
export function sortRows(rows, key = null, dir = 'asc') {
  if (!key) { return [...rows]; }
  const sorter = SORTERS[key] || SORTERS.name;
  const empty = EMPTY[key] || (() => false);
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (empty(a) !== empty(b)) { return empty(a) ? 1 : -1; }
    return sign * sorter(a, b) || compareText(a.name, b.name);
  });
}

/** A row with problems: a quality check blocks Publish, or its build failed or a service crashed. */
export const hasProblems = (row) => (row.quality.blocked || 0) > 0 || (row.problems.count || 0) > 0;

/**
 * The rows left by the search (name or owner) and the filters: `state` a run state key, `owner` 'me', 'others' or a
 * name, `problems` 'problems' (only the ones with problems); 'all' = any.
 */
export function filterRows(rows, { search = '', state = 'all', owner = 'all', problems = 'all' } = {}) {
  const needle = search.trim().toLowerCase();
  const ownerOk = (row) => {
    if (owner === 'me') { return row.owner === 'You'; }
    if (owner === 'others') { return row.owner !== 'You'; }
    return owner === 'all' || row.owner === owner;
  };
  return rows.filter((row) => {
    if (needle && !`${row.name} ${row.owner}`.toLowerCase().includes(needle)) { return false; }
    if (state !== 'all' && row.state.key !== state) { return false; }
    if (!ownerOk(row)) { return false; }
    if (problems === 'problems' && !hasProblems(row)) { return false; }
    return true;
  });
}

/** The owners to filter by: "Me" first when the person owns one, "Others" when someone else does, then each by name. */
export function ownerOptions(rows) {
  const names = [...new Set(rows.map((row) => row.owner))];
  const others = names.filter((name) => name !== 'You').sort(compareText);
  return [
    ...(names.includes('You') ? [{ value: 'me', label: 'Me' }] : []),
    ...(others.length > 0 ? [{ value: 'others', label: 'Others' }] : []),
    ...others.map((name) => ({ value: name, label: name })),
  ];
}

/** The state keys the rows are in, worst first, for the state filter: `[{ value, label }]`. */
export function stateOptions(rows) {
  const keys = [...new Set(rows.map((row) => row.state.key))].sort((a, b) => RUN_STATES[a].rank - RUN_STATES[b].rank);
  return keys.map((key) => ({ value: key, label: RUN_STATES[key].label }));
}

// ---- Quality and attention across projects ----

/** How many gate rules block Publish in a quality entry (useWorkspaceQuality), or null while it is not known. */
export function blockedOf(entry) {
  if (!entry || entry.status !== 'ready' || !entry.value?.gate) { return null; }
  const { gate } = entry.value;
  return gate.blocked ? gate.failing || 0 : 0;
}

/** The red count of the Quality tab: the blocking rules of every project whose gate is known. */
export const blockedTotal = (projects, quality) => (projects || []).reduce((sum, project) => sum + (blockedOf(quality?.[project.projectId]) || 0), 0);

/**
 * What needs the person across the workspace, worst first: a failed or crashed project (from the run states), and the
 * quality checks that block Publish (one line for all, naming the projects). `[{ id, kind, title, text, projectId? }]`;
 * empty when all is well. `answers`: run answers by project id; `quality`: useWorkspaceQuality's entries.
 */
export function attentionItems(projects, answers, quality) {
  const items = [];
  (projects || []).forEach((project) => {
    const name = project.name || project.projectId;
    const state = projectRunState(answers?.[project.projectId]);
    if (state.key === 'failed' || state.key === 'crashed') {
      items.push({ id: `run-${project.projectId}`, kind: 'build', title: state.label, text: `in ${name}`, projectId: project.projectId });
    }
  });
  const blocking = (projects || []).map((project) => ({ name: project.name || project.projectId, blocked: blockedOf(quality?.[project.projectId]) || 0 })).filter((entry) => entry.blocked > 0);
  if (blocking.length) {
    const total = blocking.reduce((sum, entry) => sum + entry.blocked, 0);
    items.push({ id: 'quality', kind: 'quality', title: `${plural(total, 'quality check')} block${total === 1 ? 's' : ''} Publish`, text: blocking.map((entry) => entry.name).join(', ') });
  }
  return items;
}

// ---- The Quality matrix ----

/** The checks of the matrix (ids of the project Quality tab, services/QualityService.js) and their column titles. */
export const MATRIX_CHECKS = [
  { id: 'tests', label: 'Tests' },
  { id: 'lint', label: 'Lint' },
  { id: 'secrets', label: 'Secrets' },
  { id: 'vulnerabilities', label: 'Vulnerabilities' },
  { id: 'dependencies', label: 'Licences' },
];

const STATUS_TONE = { pass: 'ok', fail: 'bad', warn: 'warn', error: 'warn' };
const NOT_RUN = ['skipped', 'not_ready', 'unsupported', 'offline'];

/**
 * One cell of the matrix from a check of the last run (`CheckResult`: `status`, `counts`, `metric`): `{ tone, text }`.
 * A check that did not run says "Not run", one the server did not send is `null` (shown as a dash). `missing` is how
 * many secrets the project's services still have to set (Secrets only).
 */
export function checkCell(id, check, { missing = 0 } = {}) {
  if (!check) { return null; }
  const counts = check.counts || {};
  const n = (value) => Number(value) || 0;
  if (NOT_RUN.includes(check.status)) { return { tone: 'soon', text: 'Not run' }; }
  if (id === 'tests' && n(counts.total) > 0) {
    return { tone: n(counts.failed) > 0 ? 'bad' : STATUS_TONE[check.status] || 'ok', text: `${n(counts.passed)}/${n(counts.total)}` };
  }
  if (id === 'lint') {
    if (n(counts.errors) > 0) { return { tone: 'bad', text: plural(n(counts.errors), 'error') }; }
    if (n(counts.warnings) > 0) { return { tone: 'warn', text: plural(n(counts.warnings), 'warning') }; }
    if (check.status === 'pass') { return { tone: 'ok', text: 'OK' }; }
  }
  if (id === 'secrets') {
    const parts = [];
    if (n(counts.live) > 0) { parts.push(`${n(counts.live)} live`); }
    if (missing > 0) { parts.push(`${missing} to set`); }
    if (parts.length) { return { tone: n(counts.live) > 0 ? 'bad' : 'warn', text: parts.join(' · ') }; }
    if (check.status === 'pass') { return { tone: 'ok', text: 'Clean' }; }
  }
  if (id === 'vulnerabilities') {
    if (n(counts.critical) > 0) { return { tone: 'bad', text: `${n(counts.critical)} critical` }; }
    if (n(counts.high) > 0) { return { tone: 'bad', text: `${n(counts.high)} high` }; }
    if (n(counts.medium) + n(counts.low) > 0) { return { tone: 'warn', text: `${n(counts.medium) + n(counts.low)} lower` }; }
    if (check.status === 'pass') { return { tone: 'ok', text: 'None' }; }
  }
  if (id === 'dependencies') {
    if (n(counts.notAllowed) > 0) { return { tone: 'bad', text: `${n(counts.notAllowed)} not allowed` }; }
    if (n(counts.unknown) > 0) { return { tone: 'warn', text: `${n(counts.unknown)} unknown` }; }
    if (check.status === 'pass') { return { tone: 'ok', text: 'OK' }; }
  }
  if (check.status === 'error') { return { tone: 'warn', text: 'Could not run' }; }
  return { tone: STATUS_TONE[check.status] || 'soon', text: check.metric || check.status || '—' };
}

/** The Publish column from a gate: "Ready", "Blocked by 2", "Overridden", "Not checked". */
export function publishCell(gate) {
  if (!gate) { return null; }
  if (gate.blocked) { return { tone: 'bad', text: `Blocked by ${gate.failing || 0}`, blocked: gate.failing || 0 }; }
  if (gate.override || gate.overridden) { return { tone: 'warn', text: 'Overridden', blocked: 0 }; }
  if (gate.neverRan) { return { tone: 'soon', text: 'Not checked', blocked: 0 }; }
  return { tone: 'ok', text: 'Ready', blocked: 0 };
}

/**
 * The matrix, one row per project: `{ projectId, name, status: 'loading' | 'error' | 'ready', cells: { [checkId]: {tone,
 * text} | null }, publish }`, the projects that block Publish first (most rules first), then by name. A project whose
 * checks are not read yet is `loading` (its cells say so), one the sandbox could not answer for is `error`.
 * `quality`: useWorkspaceQuality's entries read in full; `secrets`: useWorkspaceSecrets' entries.
 */
export function buildMatrix(projects, quality, secrets = {}) {
  const rows = (projects || []).map((project) => {
    const entry = quality?.[project.projectId];
    const base = { projectId: project.projectId, name: project.name || project.projectId, cells: {}, publish: null };
    if (!entry || entry.status === 'loading' || (entry.status === 'ready' && !entry.value?.checks)) { return { ...base, status: 'loading' }; }
    if (entry.status === 'error') { return { ...base, status: 'error' }; }
    const missing = secrets[project.projectId]?.status === 'ready' ? secrets[project.projectId].value.missing.length : 0;
    const cells = Object.fromEntries(MATRIX_CHECKS.map(({ id }) => [id, checkCell(id, entry.value.checks[id], { missing })]));
    return { ...base, status: 'ready', cells, publish: publishCell(entry.value.gate) };
  });
  return rows.sort((a, b) => ((b.publish?.blocked || 0) - (a.publish?.blocked || 0)) || String(a.name).localeCompare(String(b.name), 'en', { sensitivity: 'base' }));
}

/**
 * The alerts under the matrix, worst first: a failed or crashed project, then a critical or high vulnerability, then a
 * secret a service still has to set. `[{ id, tone, label, text, projectId, action: 'log' | 'quality' | 'config', button }]`.
 */
export function qualityAlerts(projects, { answers = {}, quality = {}, secrets = {} } = {}) {
  const alerts = [];
  (projects || []).forEach((project) => {
    const name = project.name || project.projectId;
    const state = projectRunState(answers[project.projectId]);
    if (state.key === 'failed' || state.key === 'crashed') {
      alerts.push({ id: `run-${project.projectId}`, tone: 'bad', label: state.label, text: name, projectId: project.projectId, action: 'log', button: 'Open log' });
    }
    const counts = quality[project.projectId]?.value?.checks?.vulnerabilities?.counts;
    const serious = (Number(counts?.critical) || 0) + (Number(counts?.high) || 0);
    if (serious > 0) {
      const parts = [];
      if (counts.critical) { parts.push(`${counts.critical} critical`); }
      if (counts.high) { parts.push(`${counts.high} high`); }
      alerts.push({ id: `vuln-${project.projectId}`, tone: 'bad', label: 'Vulnerability', text: `${parts.join(' and ')} in ${name}`, projectId: project.projectId, action: 'quality', button: 'See and fix' });
    }
    const missing = secrets[project.projectId]?.status === 'ready' ? secrets[project.projectId].value.missing : [];
    if (missing.length) {
      alerts.push({ id: `secret-${project.projectId}`, tone: 'warn', label: 'Secret missing', text: `${missing.map((entry) => entry.name).join(', ')} in ${name}`, projectId: project.projectId, action: 'config', button: 'Set it' });
    }
  });
  const rank = { log: 0, quality: 1, config: 2 };
  return alerts.sort((a, b) => rank[a.action] - rank[b.action]);
}

// ---- Activity across projects ----

/**
 * One timeline from many projects' feeds: each event keeps its fields and gains `projectId` and `projectName`, newest
 * first. `byProject`: project id -> events (or undefined while loading).
 */
export function mergeActivity(byProject, projects) {
  const names = new Map((projects || []).map((project) => [project.projectId, project.name || project.projectId]));
  const merged = [];
  Object.entries(byProject || {}).forEach(([projectId, events]) => {
    if (!names.has(projectId)) { return; }
    (Array.isArray(events) ? events : []).forEach((event) => {
      if (event && event.at) { merged.push({ ...event, projectId, projectName: names.get(projectId) }); }
    });
  });
  return merged.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

/** The most recent event time (ms) of each project: `{ [projectId]: ms }`, only for the ones with events. */
export function lastChanges(byProject) {
  const out = {};
  Object.entries(byProject || {}).forEach(([projectId, events]) => {
    const times = (Array.isArray(events) ? events : []).map((event) => Date.parse(event?.at)).filter(Number.isFinite);
    if (times.length) { out[projectId] = Math.max(...times); }
  });
  return out;
}

/** The quick filters over the activity types (collab3Model ACTIVITY_TYPES values), as the design's chips. */
export const ACTIVITY_GROUPS = [
  { id: 'all', label: 'All', types: [] },
  { id: 'code', label: 'Code changes', types: ['session', 'agent-run', 'revert'] },
  { id: 'publish', label: 'Publishes and deploys', types: ['publish', 'version'] },
  { id: 'share', label: 'Sharing and access', types: ['share'] },
  { id: 'config', label: 'Secrets and config', types: ['config'] },
];

/**
 * The merged events left by the filters: `project` ('all' or a project id), `who` ('all' or a participant key from
 * collab3Model participantOf) and `types` (type groups; empty = all).
 */
export function filterActivity(events, { project = 'all', who = 'all', types = [] } = {}) {
  const inProject = project === 'all' ? events : (events || []).filter((event) => event.projectId === project);
  return filterEvents(inProject, { who: who === 'all' ? [] : [who], types });
}

const dayKey = (date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

/** "Today", "Yesterday", else "Mon 12 Oct". */
export function dayLabel(at, now = new Date()) {
  const date = new Date(at);
  if (dayKey(date) === dayKey(now)) { return 'Today'; }
  if (dayKey(date) === dayKey(new Date(now.getTime() - 24 * 3600 * 1000))) { return 'Yesterday'; }
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Newest-first events grouped by day: `[{ key, label, events }]`. */
export function groupByDay(events, now = new Date()) {
  const groups = [];
  (events || []).forEach((event) => {
    const key = dayKey(new Date(event.at));
    const last = groups[groups.length - 1];
    if (last && last.key === key) { last.events.push(event); } else { groups.push({ key, label: dayLabel(event.at, now), events: [event] }); }
  });
  return groups;
}
